import React, { useState } from "react";
import { Row, Col, Card, Tooltip, Skeleton } from "antd";
import Highstock from "highcharts/highstock";
import ReactHighcharts from "react-highcharts";
import _ from "lodash";
import { Hra } from "../../assets/images";
import { RoundedSelect } from "../tailwind/atoms";

const Charts = ReactHighcharts.withHighcharts(Highstock);

const ClaimsSplit = ({ data = {}, loading, handleStatusChange = () => {}, currentStatus }) => {
  const numberFormatter = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
  const statusMap = ['Overall', 'Settled', 'Outstanding', 'Rejected', 'Closed']

  // Extract values safely
  const rVol = Number(data?.reimbursement?.volume || 0);
  const rAmt = Number(data?.reimbursement?.amount || 0);
  const cVol = Number(data?.cashless?.volume || 0);
  const cAmt = Number(data?.cashless?.amount || 0);
  const totalClaimAmount = data?.totalClaimAmount?.amount || null;
  const totalClaimCount = data?.totalClaimAmount?.volume || null;

  const totalVol = rVol + cVol;
  const totalAmt = rAmt + cAmt;

  // AOV calculations
  const reimbursementAov = rVol > 0 ? Math.round(rAmt / rVol) : null;
  const cashlessAov = cVol > 0 ? Math.round(cAmt / cVol) : null;
  const overallAov = totalVol > 0 ? Math.round(totalAmt / totalVol) : null;
  const avgClaimAmount = totalClaimCount > 0 ? Math.round(totalClaimAmount / totalClaimCount) : null;

  const fmtCurrency = (n) =>
    n === null ? "—" : `₹${numberFormatter.format(n)}`;

  /** Donut chart **/
  const renderDonutChart = () => {
    const chartConfig = _.cloneDeep({
      chart: { type: "pie", height: 240 },
      title: { text: "" },
      credits: { enabled: false },
      tooltip: {
        pointFormatter: function () {
          return `<b>${numberFormatter.format(this.y)}</b> (${this.percentage.toFixed(1)}%)`;
        },
      },
      plotOptions: {
        pie: {
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.point.name}: ${numberFormatter.format(this.y)}`;
            },
            style: { fontSize: "11px", color: "#555" },
          },
          colors: ["#2168f3", "#48bb78"],
        },
      },
      series: [
        {
          name: "Volume",
          size: "80%",
          innerSize: "60%",
          data: [
            { name: "Reimbursement", y: rVol },
            { name: "Cashless", y: cVol },
          ],
        },
      ],
    });

    return <Charts config={chartConfig} />;
  };

  /** Horizontal bar **/
  const renderHorizontalBar = (label, rValue, cValue, total, suffix = "", prefix = "") => {
    const safeTotal = total > 0 ? total : 1;
    const rPct = total > 0 ? (rValue / safeTotal) * 100 : 0;
    const cPct = total > 0 ? (cValue / safeTotal) * 100 : 0;

    return (
      <Row className="mt-6" align="middle">
        <Col span={24}>
          <Row style={{ marginBottom: 5 }}>
            <span className="text-gray-600 text-sm">{label}</span>
          </Row>

          <div
            style={{
              width: "100%",
              height: 20,
              backgroundColor: "#edf2f7",
              borderRadius: 6,
              display: "flex",
              overflow: "hidden",
            }}
          >
            <Tooltip title={`Reimbursement: ${prefix}${numberFormatter.format(rValue)} ${suffix}`}>
              <div
                style={{
                  width: `${rPct}%`,
                  minWidth: rPct > 0 && rPct < 4 ? 24 : undefined,
                  height: "100%",
                  backgroundColor: "#2168f3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  paddingRight: 6,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {total > 0 ? `${Math.round(rPct)}%` : "0%"}
              </div>
            </Tooltip>

            <Tooltip title={`Cashless: ${prefix}${numberFormatter.format(cValue)} ${suffix}`}>
              <div
                style={{
                  width: `${cPct}%`,
                  minWidth: cPct > 0 && cPct < 4 ? 24 : undefined,
                  height: "100%",
                  backgroundColor: "#48bb78",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  paddingRight: 6,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {total > 0 ? `${Math.round(cPct)}%` : "0%"}
              </div>
            </Tooltip>
          </div>

          <Row>
            <Col span={24} style={{ textAlign: "right", marginTop: 6 }}>
              <span style={{ color: "#6b7280", fontSize: 12 }}>
                Total: {prefix}
                {numberFormatter.format(total)} {suffix}
              </span>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <Card className="claims-split-card" style={{ marginTop: 20 }}>
      {/* Header */}
      <div className="justify-between flex items-center mb-4">
        <h1 className="text-base text-gray-700 mb-8 leading-none flex gap-4">
          <img src={Hra} className="h-6 w-6" alt="hra icon" />
          <span className="font-fig-semibold">Claim Split (Cashless vs Reimbursement)</span>
        </h1>
        <RoundedSelect
          listClasses="w-32"
          selectOptions={statusMap}
          selectValues={statusMap.map((_, i) => i)}
          curvalue={currentStatus || statusMap[0]}
          selectHandler={(index) =>
            handleStatusChange(statusMap[index])
          }
        />
      </div>

      {loading ? (
        <div className="mt-4" aria-busy="true" role="status">
          {/* Donut + Bars */}
          <Row gutter={10} style={{ marginTop: 10 }}>
            {/* Donut skeleton */}
            <Col
              span={10}
              style={{
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              <div
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  background: "#f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  boxShadow: "inset 0 0 0 8px #ffffff",
                }}
                className="animate-pulse"
              >
                {/* small placeholders in center to mimic % / count */}
                <div style={{ width: 56, height: 16, borderRadius: 8, background: "#e5e7eb", marginBottom: 6 }} className="animate-pulse" />
                <div style={{ width: 40, height: 12, borderRadius: 6, background: "#e5e7eb" }} className="animate-pulse" />
              </div>
            </Col>

            {/* Bar skeletons */}
            <Col span={12} style={{ paddingLeft: 8 }}>
              <div style={{ marginBottom: 12 }}>
                <div className="h-4 rounded w-32 bg-gray-200 animate-pulse" />
              </div>

              {/* stacked-ish bar 1 */}
              <div
                style={{
                  width: "100%",
                  height: 20,
                  borderRadius: 6,
                  background: "#e5e7eb",
                  marginBottom: 12,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div style={{ width: "44%", height: "100%", background: "#dbeafe" }} className="animate-pulse" />
                <div style={{ width: "32%", height: "100%", background: "#dcfce7", position: "absolute", right: 0 }} className="animate-pulse" />
              </div>

              {/* stacked-ish bar 2 */}
              <div
                style={{
                  width: "100%",
                  height: 20,
                  borderRadius: 6,
                  background: "#e5e7eb",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div style={{ width: "60%", height: "100%", background: "#dbeafe" }} className="animate-pulse" />
                <div style={{ width: "25%", height: "100%", background: "#dcfce7", position: "absolute", right: 0 }} className="animate-pulse" />
              </div>
            </Col>
          </Row>

          {/* AOV cards skeleton */}
          <Row gutter={10} style={{ marginTop: 20 }}>
            {Array.from({ length: 4 }).map((_, idx) => (
              <Col span={6} key={idx}>
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: 8,
                    padding: 16,
                    textAlign: "center",
                  }}
                >
                  <div className="h-3 bg-gray-200 rounded w-24 mx-auto mb-3 animate-pulse" />
                  <div className="h-6 bg-gray-200 rounded w-16 mx-auto mb-3 animate-pulse" />
                  <div className="h-3 bg-gray-200 rounded w-20 mx-auto  animate-pulse" />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      ): (
        <>
          {/* Donut + Bars */}
          <Row gutter={10} style={{ marginTop: 10 }}>
            <Col span={10} style={{ textAlign: "center", paddingLeft: 0, paddingRight: 0 }}>
              {renderDonutChart()}
            </Col>

            <Col span={12} style={{ paddingLeft: 8 }}>
              {/* Legends */}
              <Row
                style={{
                  marginBottom: 10,
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#2168f3",
                      display: "inline-block",
                    }}
                  />
                  <span style={{ fontSize: 13, color: "#374151" }}>Reimbursement</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, color: "#374151" }}>Cashless</span>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#48bb78",
                      display: "inline-block",
                    }}
                  />
                </div>
              </Row>

              {renderHorizontalBar("Count", rVol, cVol, totalVol, "claims")}
              {renderHorizontalBar("Value", rAmt, cAmt, totalAmt, "", "₹ ")}
            </Col>
          </Row>

          {/* AOV cards */}
          <Row gutter={10} style={{ marginTop: 16 }}>
            {[
              {
                label: "Reimbursement AOV",
                value: reimbursementAov,
                vol: rVol,
                amt: rAmt,
                bg: "#f0f5ff",
                color: "#1e3a8a",
                subText: "claims · ₹",
              },
              {
                label: "Cashless AOV",
                value: cashlessAov,
                vol: cVol,
                amt: cAmt,
                bg: "#f6fff0",
                color: "#166534",
                subText: "claims · ₹",
              },
              {
                label: "Overall AOV (per claim)",
                value: overallAov,
                vol: totalVol,
                amt: totalAmt,
                bg: "#fff7ed",
                color: "#92400e",
                subText: "claims · ₹",
              },
              {
                label: "Overall AOV (per claimant)",
                value: avgClaimAmount,
                vol: totalClaimCount,
                amt: totalClaimAmount,
                bg: "#fff7ed",
                color: "#92400e",
                subText: "claimants · ₹",
              }
            ].map((item, idx) => (
              <Col span={6} key={idx}>
                <div
                  style={{
                    background: item.bg,
                    borderRadius: 8,
                    padding: 10,
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{item.label}</div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: item.color,
                      marginTop: 6,
                    }}
                  >
                    {fmtCurrency(item.value)}
                  </div>
                  { (item.vol && item.amt) ? (
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    {numberFormatter.format(item.vol)} {item.subText}
                    {numberFormatter.format(item.amt)}
                  </div>) : <div style={{ height: 18, marginTop: 4 }}></div>}
                </div>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Card>
  );
};

export default ClaimsSplit;
